import * as React from 'react';

interface ApiDocsContentProps {
  apiBaseUrl: string;
  examples: ServerExample[];
  copied: string | null;
  onCopy: (value: string, label: string) => void;
}

export interface ServerExample {
  id: string;
  label: string;
  title: string;
  description: string;
  value: string;
}

type TokenKind =
  | 'comment'
  | 'function'
  | 'keyword'
  | 'number'
  | 'operator'
  | 'property'
  | 'punctuation'
  | 'string'
  | 'variable'
  | 'plain';

type HighlightToken = {
  value: string;
  kind: TokenKind;
};

const TOKEN_CLASS: Record<TokenKind, string> = {
  comment: 'text-emerald-300/70 italic',
  function: 'text-sky-200',
  keyword: 'text-fuchsia-200',
  number: 'text-amber-200',
  operator: 'text-slate-300',
  property: 'text-cyan-200',
  punctuation: 'text-slate-300',
  string: 'text-lime-200',
  variable: 'text-orange-200',
  plain: 'text-white',
};

const LANGUAGE_KEYWORDS: Record<string, Set<string>> = {
  node: new Set(['async', 'await', 'const', 'default', 'export', 'from', 'if', 'import', 'return']),
  python: new Set([
    'async',
    'await',
    'class',
    'def',
    'from',
    'if',
    'import',
    'None',
    'raise',
    'return',
    'with',
  ]),
  php: new Set(['array', 'echo', 'exit', 'false', 'function', 'if', 'null', 'return', 'true']),
  ruby: new Set(['def', 'do', 'end', 'fetch', 'nil', 'raise', 'require', 'return', 'unless']),
};

const TOKEN_PATTERN =
  /(<\?php|"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|`(?:\\.|[^`\\])*`|\/\/.*|#.*|\$[A-Za-z_][\w]*|@[A-Za-z_][\w]*|\b\d+(?:\.\d+)?\b|[A-Za-z_][\w]*|=>|===|!==|==|!=|<=|>=|&&|\|\||[{}()[\].,:;]|\s+|.)/g;

function getTokenKind(token: string, languageId: string, nextToken?: string): TokenKind {
  if (/^\s+$/.test(token)) return 'plain';
  if (token.startsWith('//') || token.startsWith('#')) return 'comment';
  if (
    (token.startsWith('"') && token.endsWith('"')) ||
    (token.startsWith("'") && token.endsWith("'")) ||
    (token.startsWith('`') && token.endsWith('`'))
  ) {
    return 'string';
  }
  if (/^\$[A-Za-z_]/.test(token) || /^@[A-Za-z_]/.test(token) || /^[A-Z_]{2,}$/.test(token)) {
    return 'variable';
  }
  if (/^\d/.test(token)) return 'number';
  if (/^[{}()[\].,:;]$/.test(token)) return 'punctuation';
  if (/^(=>|===|!==|==|!=|<=|>=|&&|\|\||[+\-*/%=<>!?:])$/.test(token)) return 'operator';
  if (LANGUAGE_KEYWORDS[languageId]?.has(token)) return 'keyword';
  if (nextToken === '(') return 'function';
  if (token === 'DECHO_BASE_URL' || token === 'DECHO_API_KEY' || token === 'X-API-Key') {
    return 'variable';
  }
  return 'plain';
}

function tokenizeCodeLine(line: string, languageId: string): HighlightToken[] {
  const tokens = line.match(TOKEN_PATTERN) ?? [line];
  return tokens.map((token, index) => ({
    value: token,
    kind: getTokenKind(token, languageId, tokens[index + 1]),
  }));
}

const CodeExample = ({ code, languageId }: { code: string; languageId: string }) => {
  const lines = code.split('\n');

  return (
    <pre className='mt-4 overflow-x-auto rounded-lg bg-slate-950 p-4 font-mono text-12 leading-5 shadow-inner'>
      <code>
        {lines.map((line, lineIndex) => (
          <React.Fragment key={`${languageId}-${lineIndex}`}>
            <span className='inline-block w-8 select-none pr-4 text-right text-slate-500'>
              {lineIndex + 1}
            </span>
            {tokenizeCodeLine(line, languageId).map((token, tokenIndex) => (
              <span key={`${lineIndex}-${tokenIndex}`} className={TOKEN_CLASS[token.kind]}>
                {token.value}
              </span>
            ))}
            {lineIndex < lines.length - 1 && '\n'}
          </React.Fragment>
        ))}
      </code>
    </pre>
  );
};

const CopyButton = ({
  copied,
  label,
  onClick,
}: {
  copied: boolean;
  label: string;
  onClick: () => void;
}) => (
  <button
    type='button'
    onClick={onClick}
    className='rounded-30 border border-gray-200 px-4 py-2 text-13 font-bold text-brand-800'
  >
    {copied ? 'Copied' : label}
  </button>
);

const FieldRow = ({
  name,
  required,
  description,
}: {
  name: string;
  required?: string;
  description: string;
}) => (
  <div className='grid grid-cols-1 gap-2 border-b border-gray-100 px-4 py-3 text-13 last:border-b-0 md:grid-cols-[180px_110px_1fr]'>
    <code className='font-mono text-brand-800'>{name}</code>
    <span className='font-semibold text-gray-600'>{required ?? '-'}</span>
    <span className='text-gray-500'>{description}</span>
  </div>
);

export const ApiDocsContent: React.FC<ApiDocsContentProps> = ({
  apiBaseUrl,
  examples,
  copied,
  onCopy,
}) => {
  const [selectedExampleId, setSelectedExampleId] = React.useState(examples[0]?.id ?? '');
  const selectedExample =
    examples.find((example) => example.id === selectedExampleId) ?? examples[0];

  const requestFields = [
    ['echo_id', 'Required', 'The echo you want to chat with.'],
    ['message', 'Required', 'The user message to send to that echo. Max 4,000 characters.'],
    ['conversation_id', 'Optional', 'Reuse this value to continue an existing API conversation.'],
    ['metadata', 'Optional', 'Your own tracking object, such as customer id or feature name.'],
    ['stream', 'Optional', 'Defaults to true for SSE. Set false to receive one JSON response.'],
  ];

  const responseFields = [
    ['id', 'Unique response id for this completion.'],
    ['conversation_id', 'Store this value and send it back to continue the same chat.'],
    ['echo.id / echo.name', 'The echo that handled the request.'],
    ['choices[0].message.content', 'The assistant reply your backend can return to your UI.'],
    ['usage.credits_charged', 'Credits charged for this request. Failed requests charge 0.'],
    ['usage.prompt_chars', 'Characters sent in the user message.'],
    ['usage.completion_chars', 'Characters returned by the echo.'],
    ['request_id', 'Trace id to keep in logs and share with support.'],
  ];

  const audioRequestFields = [
    [
      'audio',
      'Required',
      'Multipart audio file. Supported formats: WAV, MP3, OGG, WEBM, M4A, MP4. MIME parameters such as codecs are accepted. Max 10MB.',
    ],
    ['echo_id', 'Required', 'The echo you want to chat with.'],
    ['language', 'Optional', 'Speech recognition language. Defaults to auto detection.'],
    [
      'tts_provider',
      'Optional',
      'Response voice provider. Use azure or elevenlabs. Defaults to azure.',
    ],
    ['conversation_id', 'Optional', 'Reuse this value to continue an existing API conversation.'],
    ['metadata', 'Optional', 'JSON object string for your own tracking data.'],
  ];

  const audioResponseFields = [
    ['user_message.transcribed_text', 'Speech-to-text output from the uploaded audio.'],
    ['user_message.audio_url', 'Temporary secure URL for replaying the uploaded user audio.'],
    ['ai_response.content', 'Assistant text response.'],
    [
      'ai_response.audio_url_azure',
      'Temporary secure URL when Azure TTS produced the response audio.',
    ],
    [
      'ai_response.audio_url_elevenlabs',
      'Temporary secure URL when ElevenLabs produced the response audio.',
    ],
    ['ai_response.has_audio', 'True when response audio was generated successfully.'],
    ['usage.credits_charged', 'Voice-message credits charged for this request.'],
    ['request_id', 'Trace id to keep in logs and share with support.'],
  ];

  const streamingEvents = [
    [
      'message_start',
      'Sent once before text generation starts. Includes response id, echo, and conversation id.',
    ],
    ['text_chunk', 'Sent repeatedly. Append data.content to render the assistant reply live.'],
    [
      'message_end',
      'Terminal success event. Contains the same completion shape as the JSON response.',
    ],
    [
      'error',
      'Terminal stream error event after generation has started. Preflight errors still return JSON with the HTTP error status.',
    ],
  ];

  const commonErrors = [
    ['400', 'Invalid request body or blocked content.'],
    ['401', 'Missing, malformed, expired, or revoked API key.'],
    ['402', 'Insufficient credits or monthly API credit quota reached.'],
    ['403', 'Developer access disabled, missing scope, or echo not accessible.'],
    ['413', 'Audio file is larger than 10MB.'],
    ['415', 'Unsupported audio MIME type or file extension.'],
    ['429', 'Per-key minute or daily rate limit reached.'],
    ['500', 'Unexpected server error. Retry with the same X-Request-ID for traceability.'],
  ];

  const streamingExample = `const upstream = await fetch("${apiBaseUrl}/v1/chat/completions", {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    "X-API-Key": process.env.DECHO_API_KEY,
    "X-Request-ID": crypto.randomUUID()
  },
  body: JSON.stringify({
    echo_id: echoId,
    message,
    conversation_id: conversationId
  })
});

if (!upstream.ok || !upstream.body) {
  throw new Error(await upstream.text());
}

const reader = upstream.body.getReader();
const decoder = new TextDecoder();
let buffer = "";

while (true) {
  const { value, done } = await reader.read();
  if (done) break;

  buffer += decoder.decode(value, { stream: true });
  const frames = buffer.split("\\n\\n");
  buffer = frames.pop() ?? "";

  for (const frame of frames) {
    const event = frame.match(/^event: (.+)$/m)?.[1];
    const dataLine = frame.match(/^data: (.+)$/m)?.[1];
    if (!event || !dataLine) continue;

    const data = JSON.parse(dataLine);
    if (event === "text_chunk") {
      process.stdout.write(data.content);
    }
    if (event === "message_end") {
      console.log("\\nconversation:", data.conversation_id);
    }
  }
}`;

  const audioExample = `import crypto from "node:crypto";
import fs from "node:fs/promises";

const audioBuffer = await fs.readFile("./voice-message.webm");
const form = new FormData();

form.set("echo_id", echoId);
form.set("audio", new Blob([audioBuffer], { type: "audio/webm" }), "voice-message.webm");
form.set("tts_provider", "azure");
form.set("metadata", JSON.stringify({ customer_id: customerId }));

const upstream = await fetch("${apiBaseUrl}/v1/chat/audio", {
  method: "POST",
  headers: {
    "X-API-Key": process.env.DECHO_API_KEY,
    "X-Request-ID": crypto.randomUUID()
  },
  body: form
});

const data = await upstream.json();
if (!upstream.ok) {
  throw new Error(JSON.stringify(data));
}

return {
  transcript: data.user_message.transcribed_text,
  reply: data.ai_response.content,
  replyAudioUrl: data.ai_response.audio_url_azure ?? data.ai_response.audio_url_elevenlabs,
  conversationId: data.conversation_id,
  requestId: data.request_id
};`;

  return (
    <div className='flex w-full flex-col gap-8'>
      <section className='rounded-lg border border-gray-200 bg-white p-5'>
        <div className='flex flex-col gap-2'>
          <h3 className='font-sans text-[22px] font-bold text-brand-800'>Integration model</h3>
          <p className='max-w-3xl text-14 text-gray-500'>
            Keep the D.ECHO API key on your server. Browser and mobile clients should call an
            endpoint that you own, and that backend endpoint should call D.ECHO.
          </p>
        </div>

        <div className='mt-5 grid grid-cols-1 gap-4 lg:grid-cols-3'>
          {[
            ['1', 'Your frontend', 'Collects a user message and sends it to your backend.'],
            ['2', 'Your backend', 'Validates the user, attaches X-API-Key, and calls D.ECHO.'],
            ['3', 'D.ECHO API', 'Streams the echo reply, then sends usage and request id.'],
          ].map(([step, title, body]) => (
            <div key={step} className='rounded-lg bg-gray-75 p-4'>
              <div className='flex size-7 items-center justify-center rounded-full bg-brand-800 text-13 font-bold text-white'>
                {step}
              </div>
              <div className='mt-3 font-bold text-brand-800'>{title}</div>
              <div className='mt-1 text-13 text-gray-500'>{body}</div>
            </div>
          ))}
        </div>

        <div className='mt-5 rounded-lg border border-amber-100 bg-amber-50 px-4 py-3 text-14 text-amber-800'>
          Never expose `DECHO_API_KEY` in frontend JavaScript, mobile app bundles, public repos, or
          client-side environment variables.
        </div>
      </section>

      <section className='rounded-lg border border-gray-200 bg-white p-5'>
        <h3 className='font-sans text-[22px] font-bold text-brand-800'>Endpoint</h3>
        <p className='mt-1 max-w-3xl text-14 text-gray-500'>
          This is the external Chat API authenticated with `X-API-Key`. It is separate from the web
          app&apos;s JWT-authenticated endpoints.
        </p>

        <div className='mt-5 grid grid-cols-1 gap-6 xl:grid-cols-2'>
          <div className='rounded-lg border border-gray-200 p-4'>
            <div className='text-13 font-bold uppercase tracking-wide text-gray-500'>Request</div>
            <code className='mt-2 block overflow-x-auto rounded-lg bg-gray-75 p-3 font-mono text-12 text-brand-800'>
              POST {apiBaseUrl}/v1/chat/completions
            </code>
            <code className='mt-2 block overflow-x-auto rounded-lg bg-gray-75 p-3 font-mono text-12 text-brand-800'>
              POST {apiBaseUrl}/v1/chat/audio
            </code>
            <div className='mt-4 text-13 font-bold uppercase tracking-wide text-gray-500'>
              Headers
            </div>
            <div className='mt-2 overflow-hidden rounded-lg border border-gray-200'>
              <FieldRow
                name='Content-Type'
                required='Required'
                description='Use application/json for text. For voice, let your multipart client set multipart/form-data with the boundary.'
              />
              <FieldRow
                name='X-API-Key'
                required='Required'
                description='Your server-side API key from Developer settings.'
              />
              <FieldRow
                name='X-Request-ID'
                required='Optional'
                description='Your trace id for logs and support. Generate one per request.'
              />
            </div>
          </div>

          <div className='rounded-lg border border-gray-200 p-4'>
            <div className='text-13 font-bold uppercase tracking-wide text-gray-500'>
              Access rules
            </div>
            <div className='mt-3 flex flex-col gap-3 text-14 text-gray-600'>
              <p>Only accounts with developer access can create keys and call this API.</p>
              <p>Keys currently support the `chat:write` scope and inherit account credits.</p>
              <p>Each key has per-minute and per-day limits. Rate-limited calls return 429.</p>
            </div>
          </div>
        </div>
      </section>

      <section className='rounded-lg border border-gray-200 bg-white p-5'>
        <h3 className='font-sans text-[22px] font-bold text-brand-800'>Request and response</h3>

        <div className='mt-5 grid grid-cols-1 gap-6 xl:grid-cols-2'>
          <div>
            <h4 className='font-sans text-18 font-bold text-brand-800'>JSON request body</h4>
            <div className='mt-3 overflow-hidden rounded-lg border border-gray-200'>
              {requestFields.map(([name, required, description]) => (
                <FieldRow key={name} name={name} required={required} description={description} />
              ))}
            </div>
          </div>

          <div>
            <h4 className='font-sans text-18 font-bold text-brand-800'>
              JSON response fields (`stream: false`)
            </h4>
            <div className='mt-3 overflow-hidden rounded-lg border border-gray-200'>
              {responseFields.map(([name, description]) => (
                <FieldRow key={name} name={name} description={description} />
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className='rounded-lg border border-gray-200 bg-white p-5'>
        <div className='flex flex-col gap-3 md:flex-row md:items-start md:justify-between'>
          <div>
            <h3 className='font-sans text-[22px] font-bold text-brand-800'>Voice messages</h3>
            <p className='mt-1 max-w-3xl text-14 text-gray-500'>
              Use `POST /v1/chat/audio` with `multipart/form-data` when your backend needs to send a
              user voice message through the API-key surface. Voice requests return one JSON
              response after transcription, text generation, and response-audio generation complete.
            </p>
          </div>
          <CopyButton
            copied={copied === 'docs-audio'}
            label='Copy'
            onClick={() => onCopy(audioExample, 'docs-audio')}
          />
        </div>

        <div className='mt-5 grid grid-cols-1 gap-6 xl:grid-cols-2'>
          <div>
            <h4 className='font-sans text-18 font-bold text-brand-800'>Multipart request fields</h4>
            <div className='mt-3 overflow-hidden rounded-lg border border-gray-200'>
              {audioRequestFields.map(([name, required, description]) => (
                <FieldRow key={name} name={name} required={required} description={description} />
              ))}
            </div>
          </div>

          <div>
            <h4 className='font-sans text-18 font-bold text-brand-800'>JSON response fields</h4>
            <div className='mt-3 overflow-hidden rounded-lg border border-gray-200'>
              {audioResponseFields.map(([name, description]) => (
                <FieldRow key={name} name={name} description={description} />
              ))}
            </div>
          </div>
        </div>

        <div className='mt-5'>
          <h4 className='font-sans text-18 font-bold text-brand-800'>Node voice request</h4>
          <CodeExample code={audioExample} languageId='node' />
        </div>
      </section>

      <section className='rounded-lg border border-gray-200 bg-white p-5'>
        <div className='flex flex-col gap-3 md:flex-row md:items-start md:justify-between'>
          <div>
            <h3 className='font-sans text-[22px] font-bold text-brand-800'>Streaming</h3>
            <p className='mt-1 max-w-3xl text-14 text-gray-500'>
              Streaming is the default response mode. Omit `stream` or set it to `true` to receive
              `text/event-stream` chunks as the LLM responds. Set `stream: false` only when you want
              to wait for one full JSON response.
            </p>
          </div>
          <CopyButton
            copied={copied === 'docs-streaming'}
            label='Copy'
            onClick={() => onCopy(streamingExample, 'docs-streaming')}
          />
        </div>

        <div className='mt-5 grid grid-cols-1 gap-6 xl:grid-cols-2'>
          <div>
            <h4 className='font-sans text-18 font-bold text-brand-800'>SSE events</h4>
            <div className='mt-3 overflow-hidden rounded-lg border border-gray-200'>
              {streamingEvents.map(([name, description]) => (
                <FieldRow key={name} name={name} description={description} />
              ))}
            </div>
          </div>
          <div>
            <h4 className='font-sans text-18 font-bold text-brand-800'>Node stream parser</h4>
            <CodeExample code={streamingExample} languageId='node' />
          </div>
        </div>
      </section>

      <section className='rounded-lg border border-gray-200 bg-white p-5'>
        <h3 className='font-sans text-[22px] font-bold text-brand-800'>Errors</h3>
        <p className='mt-1 max-w-3xl text-14 text-gray-500'>
          Error responses use the same status code as the failed request and include a structured
          `detail` object with an error code and message when available.
        </p>

        <div className='mt-5 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3'>
          {commonErrors.map(([statusCode, description]) => (
            <div key={statusCode} className='rounded-lg border border-gray-200 p-4'>
              <div className='font-mono text-18 font-bold text-brand-800'>{statusCode}</div>
              <div className='mt-1 text-13 text-gray-500'>{description}</div>
            </div>
          ))}
        </div>
      </section>

      <section className='rounded-lg border border-gray-200 bg-white p-5'>
        <div>
          <h3 className='font-sans text-[22px] font-bold text-brand-800'>Minimal implementation</h3>
          <p className='mt-1 max-w-3xl text-14 text-gray-500'>
            Choose a backend language. These examples set `stream: false` because they return one
            JSON object. Use the streaming example above when your frontend needs live tokens.
          </p>
        </div>

        <div className='mt-5 rounded-lg border border-gray-200 bg-gray-75 p-3'>
          <div className='grid grid-cols-2 gap-2 md:flex md:flex-wrap'>
            {examples.map((example) => {
              const isActive = selectedExample?.id === example.id;
              return (
                <button
                  key={example.id}
                  type='button'
                  onClick={() => setSelectedExampleId(example.id)}
                  className={`rounded-30 border px-4 py-2 text-13 font-bold transition-colors ${
                    isActive
                      ? 'border-brand-800 bg-brand-800 text-white'
                      : 'border-gray-200 bg-white text-brand-800'
                  }`}
                >
                  {example.label}
                </button>
              );
            })}
          </div>

          {selectedExample && (
            <div className='mt-4 rounded-lg bg-white p-4'>
              <div className='flex flex-col gap-3 md:flex-row md:items-start md:justify-between'>
                <div>
                  <h4 className='font-sans text-18 font-bold text-brand-800'>
                    {selectedExample.title}
                  </h4>
                  <p className='mt-1 text-13 text-gray-500'>{selectedExample.description}</p>
                </div>
                <CopyButton
                  copied={copied === `docs-${selectedExample.id}`}
                  label='Copy'
                  onClick={() => onCopy(selectedExample.value, `docs-${selectedExample.id}`)}
                />
              </div>
              <CodeExample code={selectedExample.value} languageId={selectedExample.id} />
            </div>
          )}
        </div>
      </section>
    </div>
  );
};
